/**
 * Passwordless sign-up screen — magic-link flow.
 *
 * UX is two states:
 *   1. enter email → POST /auth/magic-link/request {intent: 'signup'}
 *   2. show "check your inbox" success view + resend button
 *
 * The actual account is created server-side when the user clicks the link
 * and lands on `/auth/magic-link?token=...`, which is handled by
 * `MagicLinkConsumeScreen.tsx`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { getPlanContext } from '@/utils/planContext';

import { authAPI } from '../../services/api';
import GoogleSignInWeb from '../../components/auth/GoogleSignInWeb';
import AppHeaderBar from '../../components/layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import {
  getStoredAttributionForAuth,
  trackAuthEvent,
  trackMagicLinkSent,
} from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';
import { rememberSignInEmail } from './magicLinkConsume';
import { brand } from '../../design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  error: '#DC2626',
  success: '#10B981',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

const landingBase = (process.env.EXPO_PUBLIC_LANDING_URL || 'https://mentorcoaching.ai').replace(/\/$/, '');
const TERMS_URL = `${landingBase}/terms`;
const PRIVACY_URL = `${landingBase}/privacy`;
const RESEND_COOLDOWN_SECONDS = 30;

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

interface Props {
  onSignUpSuccess?: () => void;
}

export default function PasswordlessSignUpScreen({ onSignUpSuccess }: Props) {
  const router = useRouter();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const [step, setStep] = useState<'email' | 'sent'>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    trackAuthEvent('signup_screen_viewed', { method: 'magic_link' });
    trackAuthEvent('passwordless_signup_screen_viewed');
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const trimmedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const sendLink = async () => {
    setError(null);
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.passwordless.signup.invalidEmail'));
      return;
    }
    setLoading(true);
    try {
      const isResend = step === 'sent';
      const attribution = await getStoredAttributionForAuth();
      trackAuthEvent('signup_started', { method: 'magic_link' });
      trackAuthEvent('magic_link_requested', { intent: 'signup' });
      // The landing plan/cycle ride on the emailed link so plan selection
      // can preselect them even if the link opens in another browser.
      const planContext = getPlanContext();
      await authAPI.requestMagicLink(trimmedEmail, {
        intent: 'signup',
        attribution,
        plan: planContext?.planTier,
        cycle: planContext?.cycle,
      });
      // If this link later turns out expired/used, the consume screen can
      // email a fresh code to the same address in one tap (AUTH-R30).
      rememberSignInEmail(trimmedEmail);
      trackAuthEvent('signup_link_sent', { method: 'magic_link' });
      trackMagicLinkSent('signup', isResend);
      setStep('sent');
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(t('auth.passwordless.signup.sendError'));
      trackAuthEvent('magic_link_request_failed', { error_code: analyticsErrorCode(err) });
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => router.push(ROUTES.PUBLIC_LOGIN as any);

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: COLORS.surface, paddingTop: insets.top }}
    >
      <AppHeaderBar />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 24,
            paddingTop: 40,
            paddingBottom: 32,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View style={{ width: '100%', maxWidth: 540, alignSelf: 'center' }}>
            <Card style={{ backgroundColor: COLORS.background, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border }}>
              <CardContent style={{ padding: 28 }}>
                {/* Branded logotype: matches the login screen — visual brand
                    identity at the top of the card so the user knows where
                    they are without re-reading the wordmark in the H1. */}
                <View
                  className="self-center mb-4"
                  style={{ width: '70%', maxWidth: 240, aspectRatio: 12439 / 4268 }}
                >
                  <Image
                    source={require('../../../assets/logotype-web.png')}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="contain"
                    accessibilityLabel="Mentor Coaching AI"
                  />
                </View>

                {/* Badge pill */}
                <View
                  className="self-center flex-row items-center px-4 py-2 mb-4 rounded-full"
                  style={{ backgroundColor: '#EFF6FF' }}
                >
                  <MaterialIcons name="schedule" size={14} color={COLORS.primary} />
                  <Text style={{ color: COLORS.primary, fontSize: 13, fontWeight: '600', marginLeft: 6 }}>
                    {t('auth.passwordless.signup.badge')}
                  </Text>
                </View>

                {step === 'email' ? (
                  <>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.primary, fontSize: 22, fontWeight: '700', marginBottom: 8 }}
                    >
                      {t('auth.passwordless.signup.title')}
                    </Text>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.secondary, fontSize: 14, lineHeight: 20, marginBottom: 24 }}
                    >
                      {t('auth.passwordless.signup.subtitle')}
                    </Text>

                    <Text style={{ color: COLORS.text.primary, fontSize: 13, fontWeight: '500', marginBottom: 8 }}>
                      {t('auth.passwordless.signup.email')}
                    </Text>
                    <View
                      className="flex-row items-center px-4 mb-3 rounded-lg"
                      style={{ backgroundColor: COLORS.background, borderWidth: 1, borderColor: COLORS.border, height: 48 }}
                    >
                      <MaterialIcons name="email" size={20} color={COLORS.text.muted} />
                      <TextInput
                        className="flex-1 ml-3 text-base"
                        style={{ color: COLORS.text.primary }}
                        placeholder={t('auth.passwordless.signup.emailPlaceholder')}
                        placeholderTextColor={COLORS.text.muted}
                        value={email}
                        onChangeText={(v) => {
                          setEmail(v);
                          if (error) setError(null);
                        }}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        editable={!loading}
                        onSubmitEditing={sendLink}
                        returnKeyType="send"
                      />
                    </View>

                    {error && (
                      <Text style={{ color: COLORS.error, fontSize: 13, marginBottom: 12 }}>{error}</Text>
                    )}

                    <Button
                      variant="default"
                      size="default"
                      onPress={sendLink}
                      disabled={loading || !email}
                      className="mb-3"
                    >
                      {loading ? (
                        <View className="flex-row items-center">
                          <ActivityIndicator size="small" color="white" />
                          <Text className="text-white font-semibold ml-2">
                            {t('auth.passwordless.signup.sending')}
                          </Text>
                        </View>
                      ) : (
                        <Text className="text-white font-semibold">
                          {t('auth.passwordless.signup.sendLink')}
                        </Text>
                      )}
                    </Button>

                    <Text className="text-center" style={{ color: COLORS.text.muted, fontSize: 12 }}>
                      <MaterialIcons name="lock" size={12} color={COLORS.text.muted} />{'  '}
                      {t('auth.passwordless.signup.linkHint')}
                    </Text>
                  </>
                ) : (
                  <>
                    <View
                      className="self-center items-center justify-center mb-4"
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 28,
                        backgroundColor: '#ECFDF5',
                      }}
                    >
                      <MaterialIcons name="mark-email-read" size={28} color={COLORS.success} />
                    </View>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.primary, fontSize: 22, fontWeight: '700', marginBottom: 8 }}
                    >
                      {t('auth.passwordless.signup.linkSentTitle')}
                    </Text>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.secondary, fontSize: 14, lineHeight: 20, marginBottom: 16 }}
                    >
                      {t('auth.passwordless.signup.linkSentBody', { email: trimmedEmail })}
                    </Text>
                    <View
                      style={{
                        backgroundColor: COLORS.surface,
                        borderRadius: 12,
                        padding: 12,
                        marginBottom: 16,
                      }}
                    >
                      <Text style={{ color: COLORS.text.secondary, fontSize: 12, lineHeight: 18 }}>
                        💡 {t('auth.passwordless.signup.linkSentTip')}
                      </Text>
                    </View>

                    <View className="flex-row justify-between items-center">
                      <TouchableOpacity
                        onPress={() => {
                          setStep('email');
                          setError(null);
                        }}
                        accessibilityRole="button"
                      >
                        <Text style={{ color: COLORS.text.secondary, fontSize: 13 }}>
                          {t('auth.passwordless.signup.changeEmail')}
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={sendLink}
                        disabled={resendIn > 0 || loading}
                        accessibilityRole="button"
                      >
                        <Text
                          style={{
                            color: resendIn > 0 ? COLORS.text.muted : COLORS.primary,
                            fontSize: 13,
                            fontWeight: '600',
                          }}
                        >
                          {resendIn > 0
                            ? t('auth.passwordless.login.resendCooldown', { seconds: resendIn })
                            : t('auth.passwordless.signup.sendAnother')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </>
                )}

                {/* Divider + OAuth (always visible — user might prefer Google) */}
                <View className="flex-row items-center my-6">
                  <View className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
                  <Text className="mx-4 text-sm" style={{ color: COLORS.text.muted }}>
                    {t('auth.passwordless.signup.or')}
                  </Text>
                  <View className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
                </View>

                <View style={{ gap: 10 }}>
                  <View className="items-center">
                    <GoogleSignInWeb
                      onSuccess={() => {
                        trackAuthEvent('signup_succeeded', { method: 'google' });
                        onSignUpSuccess?.();
                      }}
                      onError={(msg) => {
                        trackAuthEvent('signup_failed', { method: 'google', error_code: 'google_sign_in_failed' });
                        setError(msg);
                      }}
                    />
                  </View>
                </View>

                <Text
                  className="text-center mt-4"
                  style={{ color: COLORS.text.muted, fontSize: 11, lineHeight: 16 }}
                >
                  {t('auth.passwordless.signup.terms')}
                </Text>
              </CardContent>
            </Card>

            <TouchableOpacity onPress={goToLogin} className="items-center py-4" accessibilityRole="button">
              <Text style={{ color: COLORS.text.secondary, fontSize: 14 }}>
                {t('auth.register.haveAccount')}{' '}
                <Text style={{ color: COLORS.primary, fontWeight: '600' }}>
                  {t('auth.register.signIn')}
                </Text>
              </Text>
            </TouchableOpacity>

            <View className="items-center mt-2 mb-2">
              <Text className="text-xs text-center" style={{ color: COLORS.text.muted, lineHeight: 18 }}>
                <Text
                  className="text-xs underline"
                  style={{ color: COLORS.primary }}
                  onPress={() => Linking.openURL(TERMS_URL)}
                >
                  {t('profile.termsOfService')}
                </Text>
                {'  ·  '}
                <Text
                  className="text-xs underline"
                  style={{ color: COLORS.primary }}
                  onPress={() => Linking.openURL(PRIVACY_URL)}
                >
                  {t('profile.privacyPolicy')}
                </Text>
              </Text>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
